import { Box } from '@mui/material';
import { useState, useEffect, useCallback } from 'react';
import { FormProvider, useForm } from 'react-hook-form';
import { IEnhancedJobData, IJob, getSkillsSuggestions, indexJob } from 'wisestep-api';
import { WsButton } from 'wisestep-base';

import { success, warning } from '../../../utils/NotificationMessages';
import { useNotificationStore } from '../../../utils/NotificationStore';
import {
  ISkillOption,
  calculateProgress,
  mapToNormalSkillOptions,
  newSkillOptions,
  transformISkillsToStringArray,
} from '../../jobs/helper';
import AIEnhanceBar from '../../jobs/post/components/AIEnhanceBar';
import JobDescriptionComponent from '../../jobs/post/components/JobDescriptionComponent';
import SkillsAutoComplete from '../../jobs/post/components/SkillsAutoComplete';
import StarLoader from '../../jobs/post/components/StarLoader';
import { useJobCRMContext } from '../context';

const formBoxSx = {
  width: '100%',
  display: 'flex',
  flexDirection: 'column',
  gap: 2,
  p: 2,
};

const actionsBoxSx = {
  display: 'flex',
  justifyContent: 'flex-end',
  pt: 2,
};
interface JobDetailsAIEnhancementProps {
  job: IJob;
  isLoading?: boolean;
  enhancedData?: IEnhancedJobData | null;
  isAIEnhanced?: boolean;
  closeDrawer?: () => void;
}

interface IAIEnhanceFormData {
  skills: string[];
  description: string;
}

const JobDetailsAIEnhancement: React.FC<JobDetailsAIEnhancementProps> = ({
  job,
  isLoading,
  enhancedData,
  isAIEnhanced,
  closeDrawer,
}) => {
  const enhancedSkillsList = newSkillOptions(job?.skills || [], enhancedData?.requiredSkills || []);

  const [skillsOptions, setSkillsOptions] = useState<ISkillOption[]>([]);
  const methods = useForm<IAIEnhanceFormData>({
    mode: 'onChange',
    defaultValues: {
      skills: enhancedSkillsList?.map((skill) => skill.label) || job?.skills || [],
      description: enhancedData?.jobDescription || job?.description || '',
    },
  });

  const { handleSubmit, setValue, watch } = methods;
  const jobDescription = watch('description') || '';
  const selectedSkills = watch('skills') || [];
  const { getJobDetails } = useJobCRMContext();
  const [progress, setProgress] = useState<number>(
    calculateProgress(jobDescription || '', selectedSkills || []) || 0
  );

  const setNotification = useNotificationStore((state) => state.setNotification);
  const [disable, setDisable] = useState<boolean>(false);
  useEffect(() => {
    setProgress(calculateProgress(jobDescription || '', selectedSkills || []) || 0);
  }, [jobDescription, selectedSkills]);

  const onSkillInputKey = useCallback(async (skillInput: string) => {
    const result = await getSkillsSuggestions(skillInput);
    if (result.data) {
      setSkillsOptions(mapToNormalSkillOptions(transformISkillsToStringArray(result.data)));
    }
  }, []);

  const onSkillSelection = (skillTags: ISkillOption[]) =>
    setValue('skills', skillTags.map((value) => value?.label) ?? []);

  const handleSave = async (formData: IAIEnhanceFormData) => {
    setDisable(true);
    try {
      const payload = {
        ...job,
        description: formData?.description ?? '',
        skills: formData?.skills ?? [],
      };
      await indexJob(payload);
      setNotification(success('Job information saved successfully.'));
      closeDrawer && closeDrawer();
      getJobDetails();
    } catch (error) {
      console.error('Failed to save job:', error);
      setNotification(warning(`Failed to update job`));
    } finally {
      setDisable(false);
    }
  };

  useEffect(() => {
    if (enhancedData?.requiredSkills)
      setValue('skills', enhancedData?.requiredSkills || job?.skills || []);
  }, [enhancedData, job]);

  if (isLoading) {
    return (
      <Box sx={formBoxSx}>
        <StarLoader />
      </Box>
    );
  }

  if (!isAIEnhanced) return null;

  return (
    <FormProvider {...methods}>
      <Box
        component="form"
        onSubmit={handleSubmit(handleSave)}
        sx={{
          width: '100%',
          display: 'flex',
          flexDirection: 'column',
          gap: 2,
          p: 2,
        }}
      >
        <AIEnhanceBar
          progress={progress}
          title="Job Quality"
          disabled={isAIEnhanced}
          withBackground={false}
          showButton={false}
        />

        <SkillsAutoComplete
          onValueEnter={onSkillInputKey}
          onChange={onSkillSelection}
          placeholder="Type to Select Skills"
          initialValue={enhancedSkillsList}
          options={skillsOptions}
          name="skills"
          label="Skills"
          enhanceTriggered={isAIEnhanced}
        />

        <JobDescriptionComponent
          name="description"
          description={enhancedData?.jobDescription ?? ''}
          showSuccessLabel={isAIEnhanced}
        />

        <Box sx={actionsBoxSx}>
          <WsButton type="submit" variant="contained" disabled={disable}>
            Save
          </WsButton>
        </Box>
      </Box>
    </FormProvider>
  );
};

export default JobDetailsAIEnhancement;
